// src/admin/pages/roles/roleService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { Role, RoleFormData } from './role';

const ROLES_COLLECTION = 'roles';

export interface PaginatedRolesResult {
  roles: Role[];
  totalCount: number;
}

export interface RoleStats {
  total: number;
}

export class RoleService {
  /**
   * Get role statistics.
   */
  static async getRoleStats(): Promise<RoleStats> {
    try {
      const collectionRef = collection(db, ROLES_COLLECTION);
      const snapshot = await getCountFromServer(query(collectionRef));
      return { total: snapshot.data().count };
    } catch (error) {
      console.error('Error fetching role stats:', error);
      throw error;
    }
  }

  /**
   * Get paginated roles using cursor-seek (same pattern as getBlogsPaginated).
   * Supports optional text search (client-side) and page-based navigation.
   */
  static async getRolesPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string
  ): Promise<PaginatedRolesResult> {
    try {
      const collectionRef = collection(db, ROLES_COLLECTION);
      const orderConstraint = orderBy('updatedAt', 'desc');

      // ── Total count (0 doc reads) ─────────────────────────────────────────
      const countSnapshot = await getCountFromServer(query(collectionRef));
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ───────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ───────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ──────────────────────────────────
      const searchLower = searchQuery?.trim().toLowerCase() ?? '';

      const roles: Role[] = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
          } as Role;
        })
        .filter((role) => {
          if (!searchLower) return true;
          return (
            role.name.toLowerCase().includes(searchLower) ||
            role.description.toLowerCase().includes(searchLower)
          );
        });

      return { roles, totalCount };
    } catch (error) {
      console.error('Error fetching paginated roles:', error);
      throw error;
    }
  }

  /**
   * Get all roles (used for dropdowns / assignment pickers).
   */
  static async getAllRoles(): Promise<Role[]> {
    try {
      const snapshot = await getDocs(
        query(collection(db, ROLES_COLLECTION), orderBy('name', 'asc'))
      );
      return snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Role;
      });
    } catch (error) {
      console.error('Error fetching all roles:', error);
      throw error;
    }
  }

  /**
   * Get a single role by ID.
   */
  static async getRole(roleId: string): Promise<Role | null> {
    try {
      const roleRef = doc(db, ROLES_COLLECTION, roleId);
      const roleSnap = await getDoc(roleRef);

      if (!roleSnap.exists()) return null;

      const data = roleSnap.data();
      return {
        id: roleSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as Role;
    } catch (error) {
      console.error('Error fetching role:', error);
      throw error;
    }
  }

  /**
   * Check whether a role name is already taken (case-insensitive).
   * Pass `excludeId` when editing so the role being edited isn't flagged.
   */
  static async isNameTaken(name: string, excludeId?: string): Promise<boolean> {
    try {
      const snapshot = await getDocs(
        query(
          collection(db, ROLES_COLLECTION),
          where('nameLower', '==', name.trim().toLowerCase())
        )
      );
      return snapshot.docs.some((d) => d.id !== excludeId);
    } catch (error) {
      console.error('Error checking role name:', error);
      throw error;
    }
  }

  /**
   * Create a new role.
   */
  static async createRole(formData: RoleFormData, userId: string): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, ROLES_COLLECTION), {
        name: formData.name.trim(),
        nameLower: formData.name.trim().toLowerCase(),
        description: formData.description.trim(),
        permissions: formData.permissions,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }

  /**
   * Update an existing role.
   */
  static async updateRole(roleId: string, formData: RoleFormData): Promise<void> {
    try {
      const roleRef = doc(db, ROLES_COLLECTION, roleId);
      const roleDoc = await getDoc(roleRef);

      if (!roleDoc.exists()) throw new Error('Role not found');

      await updateDoc(roleRef, {
        name: formData.name.trim(),
        nameLower: formData.name.trim().toLowerCase(),
        description: formData.description.trim(),
        permissions: formData.permissions,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  /**
   * Delete a role.
   */
  static async deleteRole(roleId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, ROLES_COLLECTION, roleId));
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  }
}